import * as React from 'react';
import { connect } from 'react-redux';
import { UIEventListeners } from '../../../../../common/src/ts/types/uievent';
import {
   mapStateToViewTransform, ViewTransformSelectorResult, getVisibleRectAndScale,
} from '../../selectors/zoomingSelector';
import UIEventHandler from './UIEventHandler';
import { setRegionZoomMode } from '../../actions/UIModeAction';
import { SheetLayout, ZoomingParams } from '../../utils/view';
import ZoomingRegionSelector from './ZoomingRegionSelector';
import { Action, Dispatch } from 'redux';
import { zoomRegion } from '../../actions/ViewAction';
import { Rectangle } from 'ark-common';
import { UIModeState } from '../../reducers/uimode';
import { UIMode } from '../../../../../common/src/ts/types/UIMode';
import { drawingAreaMargin } from '../../drawing/constants';
import { AppStates } from '../../../../..';
import { zIndex, pageBackdropColor, pageWrapperBorderStyle } from '../../constants';

export enum ScaleBehavior {
  Transform,
  Resize,
}

export interface SheetScalerOwnProps {
  panelID: string;
  behavior: ScaleBehavior;
  uiEventListeners: UIEventListeners;
  uimode: UIModeState;
  zoomingParams?: ZoomingParams;

  // XXX : Extra notifications with no value changes occur.
  onScaleApply: (visibleRect: Rectangle, visualScale: number, tabPanelAreaRect: Rectangle) => void;
}

interface SheetScalerProps extends SheetScalerOwnProps, ViewTransformSelectorResult {
  dispatch: Dispatch<Action>;
  visibleRect: Rectangle;
  visualScale: number;
  tabPanelAreaRect: Rectangle;
}

const createStyle = (props: SheetScalerProps): React.CSSProperties => {
  const { behavior, arrangementInfo, transform } = props;

  const translate = `translate(${transform.translateX}px, ${transform.translateY}px)`;

  if (behavior === ScaleBehavior.Transform) {
    return {
      transformOrigin: 'left top',
      transform: `${translate} scale(${transform.scale})`,
      position: 'absolute',
    };
  }

  if (arrangementInfo.layout === SheetLayout.Fixed) {
    return {
      width: arrangementInfo.sheetWidth * transform.scale,
      height: arrangementInfo.sheetHeight * transform.scale,
      position: 'absolute',
      transform: translate,
    };
  }

  throw new Error('Scaling for reflowable contents is not supported.');
};

const createPageBackdrop = (props: SheetScalerProps): JSX.Element | null => {
  const { behavior, arrangementInfo, transform } = props;
  if (arrangementInfo.layout !== SheetLayout.Fixed) {
    return null;
  }

  const scale = behavior === ScaleBehavior.Transform ? 1 : transform.scale;
  const { sheetWidth, sheetHeight } = arrangementInfo;

  const width = sheetWidth * scale;
  const height = sheetHeight * scale;
  const margin = drawingAreaMargin * scale;

  const style: React.CSSProperties = {
    position: 'absolute',
    top: `${-margin}px`,
    left: `${-margin}px`,
    width: `${width + margin * 2}px`,
    height: `${height + margin * 2}px`,
    backgroundColor: pageBackdropColor,
    zIndex: zIndex.sheetScaler,
  };

  return <div id="pageBackdrop" style={style}></div>;
};

const wrapperStyle: React.CSSProperties = {
  overflow: 'hidden', // Prevent iframe overflow of mobile safari
};

const wrapperBorderStyle: React.CSSProperties = {
  border: pageWrapperBorderStyle,
};

const SheetScaler: React.SFC<SheetScalerProps> = (props) => {
  const style = createStyle(props);
  const { enableMargin } = props.arrangementInfo;

  // Overlay "UIEventHandler" over the iframes to handle all pointer events directly by viewer
  // (so that iframes won't receive any pointer event) in "ZoomAndPan" mode.
  const overlay = props.uimode.mode === UIMode.ZoomAndPan ? (
    <UIEventHandler uiEventListeners={props.uiEventListeners} />
  ) : null;

  const handleRegionSelect = (rect: Rectangle) => {
    props.dispatch(zoomRegion(props.panelID, rect));
    setRegionZoomMode(false, props.dispatch);
  };

  const regionSelector = props.uimode.regionZoomMode ? (
    <ZoomingRegionSelector onRegionSelected={handleRegionSelect}/>
  ) : null;

  props.onScaleApply(props.visibleRect, props.visualScale, props.tabPanelAreaRect);

  const { behavior, arrangementInfo } = props;

  let pages;
  if (behavior === ScaleBehavior.Transform) {
    pages = <div style={{ ...wrapperStyle, ...wrapperBorderStyle }}>{props.children}</div>;
  } else if (arrangementInfo.layout === SheetLayout.Fixed) {
    const { width, height } = style;
    const mergedStyle = { ...wrapperStyle, ...wrapperBorderStyle, width, height };
    pages = <div style={mergedStyle}>{props.children}</div>;
  } else {
    pages = <div style={{ ...wrapperBorderStyle }}>{props.children}</div>;
  }

  return (
    <>
      <UIEventHandler uiEventListeners={props.uiEventListeners}>
        <div style={style}>
          {enableMargin ? createPageBackdrop(props) : null}
          {pages}
        </div>
        {overlay}
      </UIEventHandler>
      {regionSelector}
    </>
  );
};

const mapStateToProps = (state: AppStates, ownProps: SheetScalerOwnProps) => {
  const transform = mapStateToViewTransform(state, ownProps);
  const [visibleRect, visualScale] = getVisibleRectAndScale(state, ownProps);
  if (ownProps.behavior === ScaleBehavior.Transform) {
    return {
      ...transform,
      visualScale,
      visibleRect: {
        x: visibleRect.x / visualScale,
        y: visibleRect.y / visualScale,
        width: visibleRect.width / visualScale,
        height: visibleRect.height / visualScale,
      },
      tabPanelAreaRect: state.view.tabPanelAreaRect,
    };
  }
  return {
    ...transform,
    visibleRect,
    visualScale: 1,
    tabPanelAreaRect: state.view.tabPanelAreaRect,
  };
};

export default connect(mapStateToProps)(SheetScaler);
